// src/features/project/MilestonesPanel.tsx
// Milestones as a due-date-ordered timeline: ListMilestones + Create / Update / Delete.

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProjectMilestone } from '@react/hooks';
import { Button, Input, Textarea, Badge, ErrorAlert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ProjectModal, Field, ProgressBar } from './ProjectModal';
import { milestoneStatusMap, idOf, listParams, toTimestamp, toDateInputValue, daysUntil } from './meta';
import { getStatusMapping, formatDate } from './logic';
import type { Entity } from '@core/types/entity';
import type { Milestone } from '@core/types/project';

export const MilestonesPanel = observer(
  ({ projectEntity, onChanged }: { projectEntity: Entity; onChanged: () => void }) => {
    const { t, locale } = useI18n();
    const store = useProjectMilestone();
    const projectId = projectEntity.id;

    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<Milestone | null>(null);
    const [deleting, setDeleting] = useState<Milestone | null>(null);
    const [busy, setBusy] = useState(false);

    const reload = () => store.ListMilestones(projectId, listParams());
    useEffect(() => {
      reload();
    }, [projectId]);

    const milestones: Milestone[] = store.items ?? [];
    const sorted = useMemo(
      () =>
        [...milestones].sort(
          (a, b) => new Date(a.due_date ?? 0).getTime() - new Date(b.due_date ?? 0).getTime(),
        ),
      [milestones],
    );

    const del = () =>
      deleting &&
      (async () => {
        setBusy(true);
        try {
          await store.DeleteMilestone(projectId, idOf(deleting));
          setDeleting(null);
          reload();
          onChanged();
        } catch {
          /* store surfaces error */
        } finally {
          setBusy(false);
        }
      })();

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">{t('project.milestones.title')}</h2>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            {t('project.milestones.create')}
          </Button>
        </div>

        {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

        {sorted.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-lg font-medium text-foreground">{t('project.milestones.empty')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('project.milestones.emptyDesc')}</p>
          </div>
        ) : (
          <ol className="relative space-y-4 border-l border-border pl-6">
            {sorted.map((m) => {
              const status = getStatusMapping(milestoneStatusMap, m.status);
              const dleft = daysUntil(m.due_date);
              return (
                <li key={idOf(m)} className="relative">
                  <span
                    className="absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-background bg-primary"
                    aria-hidden="true"
                  />
                  <div className="rounded-lg border border-border p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground">{m.name}</h3>
                        <Badge variant={status.variant as never} size="sm">
                          {t(status.labelKey)}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setEditing(m);
                            setShowForm(true);
                          }}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => setDeleting(m)}>
                          {t('common.delete')}
                        </Button>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      {formatDate(m.due_date, locale) !== '--' && <span>{formatDate(m.due_date, locale)}</span>}
                      {dleft != null && m.status === 'MILESTONE_STATUS_OPEN' && (
                        <span className={dleft < 0 ? 'text-destructive' : undefined}>
                          {dleft < 0
                            ? t('project.milestones.overdueBy').replace('{n}', String(-dleft))
                            : t('project.milestones.daysLeft').replace('{n}', String(dleft))}
                        </span>
                      )}
                    </div>
                    {m.description && <p className="mt-2 text-sm text-muted-foreground">{m.description}</p>}
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1">
                        <ProgressBar percent={m.progress_percent ?? 0} />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {m.completed_items ?? 0}/{m.total_items ?? 0}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {showForm && (
          <MilestoneFormDialog
            projectEntity={projectEntity}
            milestone={editing}
            onClose={() => setShowForm(false)}
            onSaved={() => {
              setShowForm(false);
              reload();
              onChanged();
            }}
          />
        )}

        <ConfirmDialog
          open={!!deleting}
          title={t('project.milestones.deleteTitle')}
          description={t('project.milestones.deleteDesc')}
          destructive
          isConfirming={busy}
          onConfirm={del}
          onCancel={() => setDeleting(null)}
        />
      </div>
    );
  },
);

MilestonesPanel.displayName = 'MilestonesPanel';

function MilestoneFormDialog({
  projectEntity,
  milestone,
  onClose,
  onSaved,
}: {
  projectEntity: Entity;
  milestone: Milestone | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectMilestone();
  const [name, setName] = useState(milestone?.name ?? '');
  const [description, setDescription] = useState(milestone?.description ?? '');
  const [dueDate, setDueDate] = useState(toDateInputValue(milestone?.due_date));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (milestone) {
        await store.UpdateMilestone(projectEntity.id, idOf(milestone), {
          project: projectEntity,
          milestone: { ...milestone, name: name.trim(), description, due_date: toTimestamp(dueDate) },
          field_mask: { paths: ['name', 'description', 'due_date'] },
        } as never);
      } else {
        await store.CreateMilestone(projectEntity.id, {
          project: projectEntity,
          name: name.trim(),
          description: description || undefined,
          due_date: toTimestamp(dueDate),
        } as never);
      }
      onSaved();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProjectModal
      open
      title={milestone ? t('project.milestones.editTitle') : t('project.milestones.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <Field label={t('project.milestones.name')} htmlFor="ms-name">
          <Input id="ms-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('project.milestones.description')} htmlFor="ms-desc">
          <Textarea id="ms-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </Field>
        <Field label={t('project.milestones.dueDate')} htmlFor="ms-due">
          <Input id="ms-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>
      </div>
    </ProjectModal>
  );
}
